// 🔹 Stylesheets
import './ResetPassword.scss';

// 🔹 React Core
import React, { useState } from 'react';

// 🔹 Routing
import { useNavigate, useSearchParams } from 'react-router-dom';

// 🔹 Components
import { Button } from '../../components/Button/Button';

// 🔹 Assets
import { useAuthConfirmPasswordReset } from 'src/api/mutations/useAuthConfirmPasswordReset';
import { toast } from 'src/utils/appAlert';
import { ReactComponent as PasswordVerifyIcon } from '../../../../assets/images/onboarding-trainer/password-verify.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/images/onboarding-trainer/info.svg';
import { ReactComponent as EyeClosedIcon } from '../../../../assets/images/onboarding-trainer/eye-closed.svg';
import { ReactComponent as BackIcon } from '../../../../assets/images/onboarding-trainer/back.svg';
import { ReactComponent as EyeOpenIcon } from '../../../../assets/images/onboarding-trainer/eye-open.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/images/onboarding-trainer/logo.svg';

const CheckIcon = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.6667 3.5L5.25004 9.91667L2.33337 7"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { mutateAsync: confirmReset, isPending } = useAuthConfirmPasswordReset();

  const requirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'One number', met: /[0-9]/.test(password) },
    { label: 'One special character', met: /[^A-Za-z0-9]/.test(password) },
  ];

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!oobCode) {
      toast.error('Invalid or missing password reset code.');
      return;
    }

    if (!requirements.every((req) => req.met)) {
      newErrors.password = 'Please meet all password requirements';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await confirmReset({ oobCode, newPassword: password });
      toast.success('Password successfully reset. You can now log in.');
      setIsSuccess(true);
    } catch (error: any) {
      let errorMessage = 'Failed to reset password.';
      if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'The reset link has expired or is invalid.';
      } else if (error.message) {
        errorMessage = error.message.replace('Firebase: Error ', '').trim();
      }
      setErrors({ form: errorMessage });
    }
  };

  return (
    <div className="onboarding-reset-password-container">
      <div className="reset-password-box">
        {isSuccess ? (
          <div className="reset-password-success">
            <div className="logo-placeholder">{<LogoIcon />}</div>
            <div className="success-icon-wrapper">{<PasswordVerifyIcon />}</div>
            <h1 className="reset-password-title">{'Password updated'}</h1>
            <p className="reset-password-subtitle">
              Your password has been changed successfully.
              <br />
              You can now log in with your new password.
            </p>
            <Button
              variant="outline"
              className="submit-btn m-auto back-to-login"
              onClick={() => navigate('/onboarding-trainer/login')}
            >
              {<BackIcon />} Back to Log in
            </Button>
          </div>
        ) : (
          <>
            <div className="reset-password-header">
              <div className="logo-placeholder">{<LogoIcon />}</div>
              <h1 className="reset-password-title">Create new password</h1>
              <p className="reset-password-subtitle">
                {'Choose a strong password for your miniPT account'}
              </p>
            </div>

            <form
              onSubmit={handleResetPassword}
              className="reset-password-form auth-form-common"
              noValidate
            >
              {errors.form && (
                <div className="error-message mb-2">
                  {<InfoIcon />} <span>{errors.form}</span>
                </div>
              )}

              <div className="input-group">
                <label>NEW PASSWORD</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="newPassword"
                    autoComplete="new-password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) setErrors({ ...errors, password: '' });
                    }}
                    className={errors.password ? 'error-input' : ''}
                  />
                  <button
                    type="button"
                    className="eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </button>
                </div>
              </div>

              <div className="input-group">
                <label>CONFIRM PASSWORD</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                    }}
                    className={errors.confirmPassword ? 'error-input' : ''}
                  />
                  <button
                    type="button"
                    className="eye-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="error-message">
                    {<InfoIcon />} <span>{errors.confirmPassword}</span>
                  </div>
                )}
              </div>

              <div className="password-requirements">
                <div className="requirements-title">PASSWORD MUST INCLUDE</div>
                <ul>
                  {requirements.map((req, index) => (
                    <li key={index} className={req.met ? 'met' : ''}>
                      <CheckIcon
                        color={req.met ? 'var(--primary-color, #ffa100)' : 'var(--auth-text-muted)'}
                      />
                      {req.label}
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="submit-btn bg-white"
                disabled={isPending || !password || !confirmPassword}
              >
                {isPending ? 'Resetting...' : 'Reset password'}
              </Button>
            </form>

            <Button
              variant="outline"
              className="submit-btn m-auto back-to-login"
              onClick={() => navigate('/onboarding-trainer/login')}
            >
              {<BackIcon />} Back to Log in
            </Button>
          </>
        )}
      </div>
      <div className="reset-password-bg-glow"></div>
    </div>
  );
};
